/**
 * Coverage Risk (UAT §6).
 *
 * Trading-desk coverage is measured completely differently from everything else in the
 * model, on purpose: an average utilisation number cannot see a two-hour midday crunch.
 * Each desk × time-block cell is set by hand — 0 uncovered, 1 single point of failure,
 * 2+ resilient — and the department's status reads directly off those cells.
 *
 * The Volume column is a secondary reference only. Coverage is what drives the staffing
 * signal here; the volume figure is context, and showing it any more prominently would
 * invite exactly the hours-based reasoning this screen exists to replace.
 */
import { useMemo, useState } from 'react';
import { useModel } from '../state.jsx';
import { Icon } from '../components/IconSprite.jsx';

/** 0 uncovered, 1 single point of failure, 2+ resilient, -1 not applicable. */
const CELL = {
  '-1': { bg: 'transparent', color: 'var(--ink4)', text: '—' },
  0: { bg: 'var(--ltred)', color: 'var(--red)' },
  1: { bg: '#FFFBEB', color: 'var(--amber)' },
  2: { bg: 'var(--ltgrn)', color: 'var(--grn)' },
};
const cellStyle = (v) => CELL[v >= 2 ? 2 : v] ?? CELL['-1'];

export function CoverageRisk() {
  const { state, engine, setMapValue, isAdmin } = useModel();
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('ALL');

  const grid = useMemo(() => {
    if (!engine) return null;
    return engine.TRADE_COV_ROWS.map((name, r) => ({
      name,
      r,
      deptKey: Object.keys(engine.COVERAGE_DEPT_ROW).find((k) => engine.COVERAGE_DEPT_ROW[k] === r),
      cells: engine.TRADE_COV_BLOCKS.map((_, c) => engine.getTradeCov(r, c)),
    }));
  }, [engine, state]);

  if (!grid) return null;
  const blocks = engine.TRADE_COV_BLOCKS;

  const rows = grid.filter((row) => scope === 'ALL' || row.deptKey === scope);
  const totals = rows.reduce(
    (acc, row) => {
      for (const v of row.cells) {
        if (v === 0) acc.uncovered++;
        else if (v === 1) acc.single++;
      }
      return acc;
    },
    { uncovered: 0, single: 0 },
  );

  // The grid is manually seeded and never auto-populated from a real schedule, so whether
  // anyone has checked it against reality is itself a signal.
  const verification = state.coverageVerification?.[scope] ?? state.coverageVerification?.ALL;

  async function cycle(r, c, current) {
    if (!isAdmin) return;
    const order = [0, 1, 2, -1];
    const next = order[(order.indexOf(current >= 2 ? 2 : current) + 1) % order.length];
    const key = `${r}_${c}`;
    setSaving(key);
    setError(null);
    try {
      await setMapValue('tradeCov', key, next);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  }

  async function markVerified() {
    setError(null);
    try {
      await setMapValue('coverageVerification', scope, { verifiedDate: new Date().toISOString().slice(0, 10) }, {});
    } catch (err) { setError(err.message); }
  }

  return (
    <div className="page">
      <div className="page-hdr">
        <div className="ph-row">
          <div className="ph-icon"><Icon name="ic-shield" /></div>
          <div>
            <div className="page-hdr-title">Coverage Risk</div>
            <div className="page-hdr-sub">
              Uncovered hours exposure when any person is out — planned absence, departure, or leave
            </div>
          </div>
        </div>
      </div>

      <div className="filter-bar">
        <label htmlFor="cov-dept">Department:</label>
        <select id="cov-dept" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="ALL">All Departments</option>
          {grid.map((row) => {
            const dep = state.depts.find((d) => d.id === row.deptKey);
            return dep ? <option key={dep.id} value={dep.id}>{dep.name}</option> : null;
          })}
        </select>
      </div>

      {error && <div className="alert" style={{ background: 'var(--ltred)', color: 'var(--red)' }}>{error}</div>}

      <div className="card">
        <div className="card-hdr dark">
          <Icon name="ic-shield" style={{ width: 14, height: 14 }} />
          <span style={{ marginLeft: 'auto' }}>Trading Desk Coverage Map — who covers what, when</span>
        </div>

        <div className="card-body">
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: verification ? 'var(--ltgrn)' : '#FFFBEB',
              border: `1px solid ${verification ? 'var(--grn)' : 'var(--amber)'}`,
              borderRadius: 'var(--rsm)', marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: verification ? 'var(--grn)' : 'var(--red)' }}>
              {verification
                ? `Verified against a real schedule on ${verification.verifiedDate}${verification.verifiedBy ? ` by ${verification.verifiedBy}` : ''}`
                : 'Never verified against a real schedule'}
            </span>
            {isAdmin && (
              <button className="btn btn-secondary btn-xs" style={{ marginLeft: 'auto' }} onClick={markVerified}>
                ✓ Mark as verified today
              </button>
            )}
          </div>

          <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.6, marginBottom: 6 }}>
            Staffing for trading is a <strong>coverage</strong> question, not a utilization question —
            demand peaks by time of day and product. Click any cell to set assigned coverage.{' '}
            <strong style={{ color: totals.uncovered ? 'var(--red)' : 'var(--grn)' }}>
              {totals.uncovered === 0 ? 'No uncovered blocks' : `${totals.uncovered} uncovered blocks`}
            </strong>
            {' · '}
            <strong style={{ color: totals.single ? 'var(--amber)' : 'var(--grn)' }}>
              {totals.single} single-point blocks
            </strong>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 12 }}>
            The Volume column is a <strong>secondary reference</strong> — trades, real-time volume, or
            books under management. Coverage is what drives the staffing signal; volume is context only.
          </div>

          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Desk / Product</th>
                  {blocks.map((b, i) => (
                    <th key={i} style={{ textAlign: 'center', lineHeight: 1.3 }} dangerouslySetInnerHTML={{ __html: b }} />
                  ))}
                  <th style={{ textAlign: 'right' }}>Volume<br />(reference)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const ref = engine.COVERAGE_VOL_REF[row.deptKey];
                  return (
                    <tr key={row.r}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.name}</td>
                      {row.cells.map((value, c) => {
                        const s = cellStyle(value);
                        const key = `${row.r}_${c}`;
                        return (
                          <td
                            key={c}
                            onClick={() => cycle(row.r, c, value)}
                            title={`${row.name} · ${blocks[c].replace(/<br\/?>/g, ' ')}`}
                            style={{
                              textAlign: 'center', background: s.bg, color: s.color,
                              fontFamily: "'DM Mono', monospace", fontWeight: 500,
                              cursor: isAdmin ? 'pointer' : 'default',
                              opacity: saving === key ? 0.5 : 1,
                            }}
                          >
                            {value === -1 ? '—' : value}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'right', fontSize: 11, color: 'var(--ink4)', lineHeight: 1.3 }}>
                        <span className="num-mono" style={{ color: 'var(--ink3)' }}>
                          {ref?.current?.toLocaleString() ?? '—'}
                        </span>
                        <br />
                        {ref?.name ?? ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 11, color: 'var(--ink3)' }}>
            {[
              ['0 — uncovered', 'var(--red)'],
              ['1 — single point of failure', 'var(--amber)'],
              ['2+ — resilient', 'var(--grn)'],
              ['— not applicable', 'var(--ink4)'],
            ].map(([label, color]) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 11, height: 11, border: `1.5px solid ${color}`, borderRadius: 3 }} />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="alert" style={{ background: 'var(--ltblue)', color: 'var(--ink2)' }}>
        <span>ℹ️</span>
        <span>
          Absence Exposure below uses the same legacy capacity model as Team Fragility
          (seniority multiplier, role activity %, Haircuts) — <strong>not</strong> the live
          volume/coverage signal the map above and Department Status use. Treat these hours as
          directional. A named employee's outage in a coverage department does not change a grid
          cell; it marks that department's coverage as needing revalidation instead.
        </span>
      </div>

      <div className="card">
        <div className="card-hdr grey">Absence Exposure — hours at risk if one person is out</div>
        <div className="card-body">
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th><th>Department</th><th>Role</th>
                  <th style={{ textAlign: 'right' }}>Seniority</th>
                  <th style={{ textAlign: 'right' }}>Adjusted capacity</th>
                  <th style={{ textAlign: 'right' }}>Departure risk</th>
                </tr>
              </thead>
              <tbody>
                {state.team
                  .filter((e) => !e.vacant && !e._builtStale && engine.COVERAGE_DEPT_IDS.includes(e.dept))
                  .map((e) => {
                    const dep = state.depts.find((d) => d.id === e.dept);
                    const role = state.roles.find((r) => r.id === e.roleId);
                    return (
                      <tr key={e.id}>
                        <td style={{ fontWeight: 600 }}>{e.name}</td>
                        <td>{dep?.name ?? e.dept}</td>
                        <td>{role?.name ?? e.roleId}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span className={`chip sen-${e.seniority ?? 'mid'}`}>{e.seniority ?? 'mid'}</span>
                        </td>
                        <td style={{ textAlign: 'right' }} className="num-mono">
                          {engine.empBillCapAdjusted(e).toFixed(1)} hrs
                        </td>
                        <td style={{ textAlign: 'right' }} className="num-mono">
                          {Math.round((e.deptProb ?? 0) * 100)}%
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
